import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import BrandLogo from './BrandLogo';
import type { RoleContext, RoleEnum, RoleSelection } from '~/types';

interface TopbarProps {
  school: { id?: string; name?: string; slug?: string } | null;
  roleContext?: RoleContext | null;
  setSelection?: (selection: RoleSelection) => void;
  onMenuToggle: () => void;
  sidebarOpen: boolean;
  onSearchClick: () => void;
}

function Topbar({ school, roleContext, setSelection, onMenuToggle, sidebarOpen, onSearchClick }: TopbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolSlug } = useParams();

  // Years the current role can work in, so the header only offers years that
  // the pages behind it will accept.
  const accessibleSchoolYears = useMemo(() => {
    const accessibleSchool = roleContext?.accessibleSchools?.find(
      (as) => as.school.id === (roleContext?.selectedSchool?.id ?? school?.id)
    );
    const role = (roleContext?.selectedRole || '') as RoleEnum;
    return accessibleSchool?.accessibleSchoolYears
      ?.filter((asy) => asy.roles.includes(role))
      ?.map((asy) => asy.schoolYear) || [];
  }, [roleContext, school?.id]);

  const selectedYear = roleContext?.selectedSchoolYear ?? null;

  // Switching years keeps the admin on the same page for the new year when the
  // page is year-scoped (/years/:yearSlug/...), and otherwise leaves them where
  // they are — the year is context, not a destination.
  const handleYearChange = (yearId: string) => {
    const year = accessibleSchoolYears.find((y) => y.id === yearId);
    if (!year) return;
    setSelection?.({ schoolYearId: year.id });

    const yearScoped = location.pathname.match(/^\/app\/([^/]+)\/years\/[^/]+(\/.*)?$/);
    if (yearScoped && schoolSlug) {
      navigate(`/app/${schoolSlug}/years/${year.slug}${yearScoped[2] ?? ''}`);
    }
  };

  return (
    <>
      <nav className="relative flex items-center gap-2 px-3 py-3 sm:px-4 md:justify-between">
        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="mobile-menu-toggle md:hidden"
            onClick={onMenuToggle}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <BrandLogo />
        </div>

        {/*
          * On a phone there is no room to centre the school name between the
          * menu button and the search: it collided with both and wrapped out of
          * the header. So it sits in the flow there, truncated, and only takes
          * the centre of the header once there is a centre to take.
          */}
        <div className="flex min-w-0 flex-1 items-center gap-2 md:absolute md:left-1/2 md:flex-none md:-translate-x-1/2 md:gap-3">
          <h1 className="min-w-0 truncate text-base font-semibold text-foreground md:text-xl">
            {school?.name || 'Loading...'}
          </h1>
          {accessibleSchoolYears.length > 0 && (
            <Select value={selectedYear?.id ?? ''} onValueChange={handleYearChange}>
              <SelectTrigger className="h-8 w-auto gap-1.5 px-2.5 text-sm text-muted-foreground" aria-label="School year">
                <SelectValue placeholder="Select a year" />
              </SelectTrigger>
              <SelectContent>
                {accessibleSchoolYears.map((year) => (
                  <SelectItem key={year.id} value={year.id}>
                    {year.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="icon"
            className="relative h-9 w-9 justify-center px-0 text-muted-foreground sm:w-32 sm:justify-start sm:px-3 sm:pr-12 sm:text-sm lg:w-48"
            onClick={onSearchClick}
          >
            <Search className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Search...</span>
            <kbd className="pointer-events-none absolute right-1.5 top-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              <span className="text-xs">&#x2318;</span>K
            </kbd>
          </Button>
        </div>
      </nav>
    </>
  );
}

export default Topbar;
